import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useStaffTaxCases } from '../hooks/useStaffTaxCases';
import { useFirmUpdates } from '../hooks/useFirmUpdates';
// Fix: changed ghlSyncUsersNow to crmSyncUsersNow to match exported members in services/firestoreService
import { addFirmUpdate, deleteFirmUpdate, crmSyncUsersNow } from '../services/firestoreService';
import { TaxStatus, UserRole } from '../types';

interface StaffDashboardProps {
  onCaseSelect: (caseId: string) => void;
  onNavigate: (view: string) => void;
  locationId?: string | null;
}

const StaffDashboard: React.FC<StaffDashboardProps> = ({ onCaseSelect, onNavigate, locationId }) => {
  const { user, userDoc, firmSettings, logout, isFirmOwner, staffRole } = useAuth();
  const brandColor = firmSettings.brandColor || '#27b3e6';

  const activeLocationId = locationId || userDoc?.locationId || userDoc?.firmId;
  const isDemo = activeLocationId === 'hannah-financial-demo';

  // ✅ Robust role resolution
  const rawRole =
    (userDoc as any)?.role ||
    (userDoc as any)?.filershubRole ||
    staffRole ||
    null;

  const currentStaffRole = rawRole as UserRole;

  const isPrivileged =
    isFirmOwner ||
    staffRole === "Manager" ||
    currentStaffRole === UserRole.FirmOwner ||
    currentStaffRole === UserRole.Manager;

  const { cases: activeCases } = useStaffTaxCases(user?.uid, currentStaffRole, 'my_open', activeLocationId);
  const { cases: completedCases } = useStaffTaxCases(user?.uid, currentStaffRole, 'my_completed', activeLocationId);
  const { updates, loading: updatesLoading } = useFirmUpdates();

  const [newUpdateTitle, setNewUpdateTitle] = useState('');
  const [newUpdateContent, setNewUpdateContent] = useState('');
  const [isPosting, setIsPosting] = useState(false);

  // ✅ Sync button state
  const [syncingTeam, setSyncingTeam] = useState(false);

  const pendingSigs = activeCases.filter(c => c.status === TaxStatus.READY_TO_SIGN).length;
  const inReview = activeCases.filter(c => c.status === TaxStatus.IN_REVIEW).length;

  const handlePostUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeLocationId || !newUpdateTitle.trim() || !newUpdateContent.trim()) return;

    setIsPosting(true);
    try {
      await addFirmUpdate(activeLocationId, {
        title: newUpdateTitle,
        content: newUpdateContent,
        authorName: userDoc?.displayName || 'Firm Admin'
      });
      setNewUpdateTitle('');
      setNewUpdateContent('');
    } catch (e) {
      alert("Failed to post update.");
    } finally {
      setIsPosting(false);
    }
  };

  const handleDeleteUpdate = async (updateId: string) => {
    if (!activeLocationId || !window.confirm("Are you sure you want to remove this announcement?")) return;
    try {
      await deleteFirmUpdate(activeLocationId, updateId);
    } catch (e) {
      alert("Failed to delete update.");
    }
  };

  // ✅ Sync GHL Team handler
  const handleSyncGhlTeam = async () => {
    if (!activeLocationId) return;

    setSyncingTeam(true);
    try {
      if (isDemo) {
        // Simulate a delay for demo purposes
        await new Promise(resolve => setTimeout(resolve, 1500));
      } else {
        await crmSyncUsersNow({ locationId: activeLocationId });
      }
      alert("GHL Team sync complete.");
    } catch (e) {
      console.error(e);
      alert("GHL Team sync failed.");
    } finally {
      setSyncingTeam(false);
    }
  };

  const StatCard = ({ title, value, icon, color }: { title: string, value: number, icon: string, color: string }) => (
    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-6">
      <div className={`w-14 h-14 rounded-2xl ${color} flex items-center justify-center text-2xl shadow-sm`}>
        {icon}
      </div>
      <div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{title}</p>
        <p className="text-3xl font-bold text-slate-800 tabular-nums">{value}</p>
      </div>
    </div>
  );

  // ✅ Prevent crash if role is missing
  const roleLabel = (rawRole ? String(rawRole) : "Staff").replace('_', ' ');

  return (
    <div className="max-w-full mx-auto space-y-8">
      {/* Simulation Header */}
      {isDemo && (
        <div className="bg-emerald-500 text-white px-6 py-3 rounded-2xl flex items-center justify-between shadow-lg shadow-emerald-500/20 border border-emerald-400">
          <div className="flex items-center gap-3">
            <span className="text-xl">🛠️</span>
            <div>
              <p className="text-xs font-black uppercase tracking-widest leading-none">Simulation Active</p>
              <p className="text-[11px] font-medium opacity-90">Hannah Financial Developer Sandbox Environment</p>
            </div>
          </div>
          <div className="px-3 py-1 bg-white/20 rounded-lg text-[10px] font-black uppercase tracking-tighter">
            Verified Identity: {roleLabel}
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Filers Hub</h1>
          <p className="text-slate-500 font-medium tracking-tight">
            Welcome, {userDoc?.displayName}.
            {activeLocationId && <span className="ml-2 text-xs text-slate-400 font-semibold uppercase tracking-widest">Context: {activeLocationId}</span>}
          </p>
        </div>

        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2 px-4 py-2 bg-brand-50 rounded-xl border border-brand-100">
            <span className="w-2 h-2 rounded-full bg-brand-500 animate-pulse"></span>
            <span className="text-[10px] font-bold text-brand-700 uppercase tracking-widest">Secure Session Active</span>
          </div>

          <div className="flex items-center gap-2">
            {/* ✅ Sync GHL Team button (privileged users only) */}
            {isPrivileged && (
              <button
                onClick={handleSyncGhlTeam}
                disabled={syncingTeam}
                className="px-4 py-2 rounded-xl bg-slate-900 text-white text-[10px] font-bold uppercase tracking-widest hover:bg-black transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {syncingTeam ? "Syncing..." : <><span>🔄</span> Sync GHL Team</>}
              </button>
            )}

            {userDoc && (
              <button
                onClick={() => onNavigate('account')}
                className="flex items-center gap-3 bg-white px-4 py-2 rounded-xl shadow-sm border border-slate-200 transition-all hover:border-brand-300 hover:shadow-md active:scale-95 group/profile"
              >
                <img
                  src={userDoc.avatarUrl || "https://via.placeholder.com/64"}
                  alt="Avatar"
                  className="w-8 h-8 rounded-full border object-cover shadow-inner transition-transform group-hover/profile:scale-110"
                  style={{ borderColor: brandColor }}
                />
                <div className="text-left leading-none">
                  <p className="text-xs font-bold text-slate-800 uppercase tracking-tight group-hover/profile:text-brand-600">{userDoc.displayName}</p>
                  <p className="text-[9px] text-slate-400 font-semibold uppercase tracking-[0.05em] mt-1">
                    {roleLabel}
                  </p>
                </div>
              </button>
            )}
          </div>
          
          <button
            onClick={logout}
            className="px-2 text-[9px] font-bold text-slate-400 hover:text-red-500 uppercase tracking-[0.15em] transition-colors flex items-center gap-1.5 self-end"
          >
            Logout & Switch Role
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title="Active Returns" value={isDemo ? 12 : activeCases.length} icon="📁" color="bg-indigo-50 text-indigo-600" />
        <StatCard title="In Review" value={isDemo ? 4 : inReview} icon="🔍" color="bg-amber-50 text-amber-600" />
        <StatCard title="Pending Signatures" value={isDemo ? 7 : pendingSigs} icon="✍️" color="bg-rose-50 text-rose-600" />
        <StatCard title="Total Completed" value={isDemo ? 142 : completedCases.length} icon="✅" color="bg-emerald-50 text-emerald-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-slate-800 uppercase tracking-widest text-xs">Recent Pipeline Activity</h3>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm divide-y divide-slate-100 overflow-hidden">
            {activeCases.map(c => (
              <div
                key={c.id}
                className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors group cursor-pointer"
                onClick={() => onCaseSelect(c.id)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-lg grayscale group-hover:grayscale-0 transition-all">👤</div>
                  <div>
                    <p className="font-bold text-slate-800 text-sm tracking-tight">{c.clientName}</p>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tight">{c.year} • {c.filingType}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-[9px] font-bold uppercase text-slate-400 tracking-widest">
                    Status: <span className="text-brand-600">{c.status}</span>
                  </span>
                  <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-brand-600 group-hover:text-white transition-all">→</div>
                </div>
              </div>
            ))}
            {activeCases.length === 0 && (
              <div className="p-12 text-center text-slate-400 italic text-sm font-medium">No active returns currently in your pipeline.</div>
            )}
          </div>

          {isDemo && (
             <div className="bg-brand-50 rounded-2xl p-6 border border-brand-100 flex items-center gap-6">
                <div className="text-3xl">💡</div>
                <div>
                   <h4 className="font-bold text-brand-900 text-sm uppercase tracking-wider">Developer Tip</h4>
                   <p className="text-brand-700 text-xs mt-1 font-medium">This sandbox resets every session. You can test all features including document signatures, GHL team sync simulation, and real-time announcements without affecting production data.</p>
                </div>
             </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 overflow-hidden">
            <h3 className="font-bold text-slate-800 uppercase tracking-widest text-xs mb-6 flex items-center gap-2">
              <span>📢</span> Firm Announcements
            </h3>

            {isPrivileged && (
              <form onSubmit={handlePostUpdate} className="mb-8 space-y-3 bg-slate-50 p-4 rounded-xl border border-slate-100">
                <input
                  required
                  placeholder="Announcement Title"
                  value={newUpdateTitle}
                  onChange={(e) => setNewUpdateTitle(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-brand-500"
                />
                <textarea
                  required
                  placeholder="Message to all clients..."
                  rows={3}
                  value={newUpdateContent}
                  onChange={(e) => setNewUpdateContent(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                />
                <button
                  disabled={isPosting}
                  className="w-full bg-slate-900 text-white py-2 rounded-lg text-xs font-bold uppercase tracking-widest hover:bg-black transition-all disabled:opacity-50"
                >
                  {isPosting ? 'Posting...' : 'Broadcast Announcement'}
                </button>
              </form>
            )}

            <div className="space-y-6 max-h-[400px] overflow-y-auto pr-2 scrollbar-hide">
              {updatesLoading ? (
                <div className="animate-pulse space-y-4">
                  <div className="h-4 bg-slate-100 w-3/4 rounded"></div>
                  <div className="h-10 bg-slate-50 w-full rounded"></div>
                </div>
              ) : updates.length === 0 ? (
                <p className="text-center text-xs text-slate-400 italic">No announcements currently visible to clients.</p>
              ) : (
                updates.map(update => (
                  <div key={update.id} className="relative group border-l-2 pl-4" style={{ borderColor: brandColor }}>
                    {isPrivileged && update.id !== 'u1' && update.id !== 'u2' && (
                      <button
                        onClick={() => handleDeleteUpdate(update.id)}
                        className="absolute right-0 top-0 text-slate-300 hover:text-red-500 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                    <p className="text-[10px] text-slate-400 font-bold mb-1">
                      {update.createdAt?.toDate ? update.createdAt.toDate().toLocaleDateString() : 'Just now'}
                    </p>
                    <h4 className="text-sm font-bold text-slate-800 leading-tight">{update.title}</h4>
                    <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{update.content}</p>
                    {update.authorName && <p className="text-[9px] text-slate-400 font-bold uppercase mt-2">— {update.authorName}</p>}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 text-white shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <span className="text-6xl font-bold">📅</span>
            </div>
            <h4 className="font-bold uppercase tracking-[0.2em] text-xs mb-6 text-brand-400">Tax Deadlines</h4>
            <div className="space-y-6">
              <div className="border-l-2 border-brand-500 pl-4">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">March 15, 2025</p>
                <p className="text-sm font-bold tracking-tight">S-Corp / Partnership</p>
              </div>
              <div className="border-l-2 border-slate-700 pl-4">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">April 15, 2025</p>
                <p className="text-sm font-bold tracking-tight text-slate-300">Individual Tax Filing</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffDashboard;
